import {
  Dynamic,
  EMPTY_VALUE,
  FieldName,
  FieldNames,
  FieldSchema,
  FieldValue,
  FieldValues,
  Schema,
} from "../Schema";
import type { DataCardinalitySchema, StandardEnumOption } from "../data/DataManager";
import { cloneDeep } from "lodash";
import {
  defaultValidationFieldStatus,
  ErrorMessage,
  ValidationFieldStatus,
  ValidationManager,
  ValidationDateSchema,
} from "../validation/ValidationManager";
import {
  DataEnumSchema,
  DataFieldState,
  DataManager,
  DataStringSchema,
  DataVariants,
  defaultDataFieldState,
} from "../data/DataManager";
import { BasicState, Mapping, DynamicManager, CustomState } from "../common/DynamicManager";

export const InputTypes = {
  TEXT: "text",
  TEXTAREA: "textarea",
  SELECT: "select",
  MULTISELECT: "multiselect",
  DATE: "date",
  SWITCH: "switch",
  INDICATOR: "indicator",
  CUSTOM: "custom",
};

export type InputType = typeof InputTypes[keyof typeof InputTypes];

export enum Widths {
  FULL = "full",
  THREE_QUARTERS = "threeQuarters",
  HALF = "half",
  DOUBLE = "double",
  NORMAL = "normal",
}

export interface FormBasicSchema {
  type?: InputType;
  label?: string;
  helperText?: string;
  tooltip?: string;
  width?: Widths;
  disabled?: boolean;
  displayed?: boolean;
}

export interface FormBasicState extends BasicState, FormBasicSchema {
  name: FieldName;
  required?: boolean;
  valid?: boolean;
  active?: boolean;
  errorMessages?: ErrorMessage[];
}

export interface FormTextSchema extends FormBasicSchema {
  type?: typeof InputTypes.TEXT;
}

export interface FormTextareaSchema extends FormBasicSchema {
  type: typeof InputTypes.TEXTAREA;
}

export type SelectOption = StandardEnumOption<FieldValue>;

export interface FormSelectSchema extends FormBasicSchema {
  type: typeof InputTypes.SELECT;
}

export interface FormMultiselectSchema extends FormBasicSchema {
  type: typeof InputTypes.MULTISELECT;
}

export interface FormDateSchema extends FormBasicSchema {
  type: typeof InputTypes.DATE;
  minDate?: Date;
  maxDate?: Date;
}

export interface FormSwitchSchema extends FormBasicSchema {
  type: typeof InputTypes.SWITCH;
}

export interface FormIndicatorSchema extends FormBasicSchema {
  type: typeof InputTypes.INDICATOR;
}

export interface FormCustomInputSchema extends FormBasicSchema {
  type: typeof InputTypes.CUSTOM;
  render: any;
}

export type FormSchema = FormTextSchema |
  FormTextareaSchema |
  FormSelectSchema |
  FormMultiselectSchema |
  FormDateSchema |
  FormSwitchSchema |
  FormIndicatorSchema |
  FormCustomInputSchema;

export interface FormTextState extends FormBasicState {
  maxLength: number;
}

export interface FormTextareaState extends FormBasicState {
  maxLength: number;
}

export interface FormSelectState extends FormBasicState {
  options: SelectOption[];
}

export interface FormDateState extends FormBasicState {
  minDate?: Date;
  maxDate?: Date;
}

export interface FormMultiselectState extends FormBasicState {
  options: SelectOption[];
  maxCount: number;
}

export type FormFieldState = FormSchema &
  (
    FormBasicState |
    FormTextState |
    FormTextareaState |
    FormSelectState |
    FormMultiselectState
  );

export type FormFieldsState = Record<string, FormFieldState>;

export const defaultFormFieldState: FormFieldState = {
  name: "default",
  type: InputTypes.TEXT,
  label: "",
  helperText: "",
  tooltip: "",
  width: Widths.NORMAL,
  loading: false,
  required: false,
  disabled: false,
  valid: false,
  displayed: true,
  active: true,
  errorMessages: [],
}

export interface FormState extends CustomState {
  valid: boolean;
  values: FieldValues;
  errorMessages: Record<string, ErrorMessage[]>;
}

export const defaultFormState: FormState = {
  loading: true,
  valid: false,
  values: {},
  errorMessages: {},
};

/**
 * Manager for handling form-related state and logic based on a given schema.
 * It extends the SchemaManager to use its state management capabilities
 * and integrates DataManager and ValidationManager for handling field values
 * and validation status respectively.
 */
export class FormManager extends DynamicManager<FormFieldState, FormState> {
  /**
   * Custom state summarizing the state of the form, including loading status,
   * overall validity, all error messages, and current field values.
   */
  customState: FormState = defaultFormState;

  /**
   * Instance of Validation Manager for handling validation logic.
   */
  validationManager: ValidationManager;

  /**
   * Instance of Data Manager for handling field data and values logic.
   */
  dataManager: DataManager;

  /**
   * Callbacks for updating field inputs when their state changes.
   */
  fieldInputs: Record<FieldName, (() => void)[]> = {};

  /**
   * Callbacks for updating form instances when the form state changes.
   */
  formInstances: Record<string, () => void> = {};

  /**
   * Customized constructor for the FormManager, which initializes the DataManager
   * and ValidationManager and merge subscriptions from all managers.
   *
   * @param schema - Schema that the FormManager is working with.
   */
  constructor(
    schema: Schema = {}
  ) {
    super(schema);
    this.dataManager = new DataManager(schema);
    this.validationManager = new ValidationManager(schema);
    Object.entries(schema)
      .forEach(
        ([fieldName]) => {
          this.subscriptions[fieldName] = Array.from(new Set([
            ...(this.subscriptions?.[fieldName] || []),
            ...(this.dataManager.subscriptions?.[fieldName] || []),
            ...(this.validationManager.subscriptions?.[fieldName] || [])
          ]));
        }
      );
    this.dataManager.subscriptions = this.subscriptions;
    this.validationManager.subscriptions = this.subscriptions;
  }

  /**
   * Customized initialization method for the FormManager, which initializes
   * the DataManager and ValidationManager before calling the base initialization logic.
   *
   * @param initialValues - Initial values to set for the form fields
   *                        during initialization.
   */
  async init(initialValues: FieldValues) {
    await this.dataManager.init(initialValues);
    const sanitizedValues = this.dataManager.getValues() || {};
    await this.validationManager.init(sanitizedValues);
    return await super.init(sanitizedValues);
  }

  /**
   * Updates the custom state of the form based on the current processing status,
   * field values, overall validity, and error messages. It retrieves the necessary
   * information from the DataManager and ValidationManager and then triggers updates
   * for all form instances to reflect the new state.
   */
  async updateCustomState() {
    const validationState = this.validationManager.getCustomState();
    this.customState = {
      loading: this.loading,
      values: this.dataManager.getValues() || {},
      valid: validationState?.valid || false,
      errorMessages: validationState?.errorMessages || {},
    }
    this.updateFormInstances();
  }

  /**
   * Generates a mapping for a form field"s state based on its type and other
   * schema properties. It creates a basic mapping for common field properties
   * and then extends it with type-specific properties.
   * It also uses the ValidationManager and DataManager to include dynamic
   * properties like validation status and options.
   *
   * @param field - Field schema to generate the mapping for.
   * @returns A mapping for the form field state, including both basic and
   *          type-specific properties.
   */
  getMapping(field: FieldSchema): Mapping<FormFieldState> {
    const basicMapping: Mapping<FormBasicState> = {
      name: field.name,
      type: field.form?.type ?? InputTypes.TEXT,
      label: field.form?.label ?? field.name,
      helperText: field.form?.helperText,
      tooltip: field.form?.tooltip,
      displayed: field.form?.displayed ?? true,
      required: field.validation?.required ?? false,
      disabled: field.form?.disabled ?? false,
      width: field.form?.width ?? Widths.NORMAL,
      active: () => {
        const active = this.getDataState(field.name).active;
        return active ?? false;
      },
      valid: () => this.getValidationStatus(field.name).valid ?? true,
      errorMessages: () => this.getValidationStatus(field.name).errorMessages ?? [],
    };
    return this.getTypeSpecificMapping(field, basicMapping);
  }

  /**
   * Generates a type-specific mapping for a form field based on its schema.
   *
   * @param field - Field schema to generate the mapping for.
   * @param basicMapping - Basic mapping for common field properties.
   * @returns A mapping for the form field state, including both basic and
   *          type-specific properties.
   */
  getTypeSpecificMapping(
    field: FieldSchema,
    basicMapping: Mapping<FormBasicState>,
  ): Mapping<FormFieldState> {
    if (field.form?.type === InputTypes.INDICATOR) {
      if (field.data?.variant === DataVariants.ENUM) {
        return {
          ...basicMapping,
          options: () => (
            this.getDataState(field.name) as DataEnumSchema
          ).options as StandardEnumOption<FieldValue>[] ?? [],
        };
      }
    }
    switch (field.form?.type) {
      case InputTypes.SELECT:
      case InputTypes.MULTISELECT:
        return {
          ...basicMapping,
          options: () => (
            this.getDataState(field.name) as DataEnumSchema
          ).options as StandardEnumOption<FieldValue>[] ?? [],
          maxCount: (field.data as DataCardinalitySchema)?.maxCount ?? undefined,
        };
      case InputTypes.DATE:
        // Passed through rather than resolved here: either bound may be a getter, and the
        // mapping is prepared by `prepareDynamicPropertyValue` on the way into the state
        // generator, so the picker receives a resolved Date either way.
        return {
          ...basicMapping,
          minDate: (field.validation as Dynamic<ValidationDateSchema>)?.minDate ?? undefined,
          maxDate: (field.validation as Dynamic<ValidationDateSchema>)?.maxDate ?? undefined,
        };
      case InputTypes.TEXT:
      case InputTypes.TEXTAREA:
      default:
        return {
          ...basicMapping,
          maxLength: (field.data as DataStringSchema)?.maxLength ?? undefined,
        };
    }
  }

  /**
   * Retrieves the current state of a specific field from the DataManager.
   *
   * @param name - The name of the field to retrieve the state for.
   * @returns The current state of the specified field, or a default state
   *          if not available.
   */
  getDataState(
    name: FieldName
  ): DataFieldState {
    return this.dataManager.getFieldState(name) ??
      defaultDataFieldState;
  }

  /**
   * Retrieves the current validation status of a specific field from the
   * ValidationManager.
   *
   * @param name - The name of the field to retrieve the validation status for.
   * @returns The current validation status of the specified field, or a default
   *          status if not available.
   */
  getValidationStatus(
    name: FieldName
  ): ValidationFieldStatus {
    return this.validationManager.getFieldValidationStatus(name) ??
      defaultValidationFieldStatus;
  }

  /**
   * Updates the form input representations for a specific field by triggering all
   * registered callbacks for that field.
   *
   * @param name - The name of the field to update the input representations for.
   */
  updateFieldInputs(
    name: FieldName
  ) {
    for (const callback of this.fieldInputs[name] || []) {
      callback();
    }
  }

  /**
   * Updates the form instances by triggering all registered callbacks for the
   * form instances. This method is called whenever the custom state of the form
   * is updated to ensure that all form instances reflect the latest state.
   */
  updateFormInstances() {
    Object.entries(this.formInstances)
      .forEach(([, callback]) => {
        callback()
      });
  };

  /**
   * Retrieves the current custom state of the form, which includes loading status,
   * overall validity, all error messages, and current field values.
   *
   * @returns The current custom state of the form.
   */
  public getFormState(): FormState {
    return cloneDeep(this.customState);
  }

  /**
   * Retrieves the current state of all fields in the form.
   *
   * @returns An object containing the current state of all fields in the form.
   */
  public getFieldsState(): FormFieldsState {
    return cloneDeep(this.state);
  }

  /**
   * Retrieves the current values of all fields in the form from the DataManager.
   *
   * @returns An object containing the current values of all fields in the form.
   */
  public getFormValues(): FieldValues {
    return cloneDeep(this.dataManager.getValues() ?? {});
  }

  /**
   * Retrieves the current value of a specific field in the form from the DataManager.
   *
   * @param name - name of the field to retrieve the value for.
   * @returns The current value of the specified field in the form, or null
   *          if not available.
   */
  public getFieldValue(
    name: FieldName
  ): FieldValue {
    return cloneDeep(this.dataManager.getValues()[name] ?? EMPTY_VALUE);
  }

  /**
   * Sets the loading status for a list of fields and updates their input
   * representations accordingly. If needed, it will apply loading status
   * recursively to all subscribed fields .
   *
   * @param fieldNames - The list of field names to set the loading status for.
   * @param loading - The loading status to set for the specified fields.
   * @param recurring - A flag indicating whether the loading status should
   *                      be applied recursively.
   * @param triggerFields - list of fields that triggered the loading update.
   */
  public setFieldsLoading(
    fieldNames: string[],
    loading: boolean,
    recurring: boolean = false,
    triggerFields: FieldNames = []
  ) {
    for (const fieldName of fieldNames){
      if (this.state[fieldName] && !triggerFields.includes(fieldName)) {
        this.state[fieldName].loading = loading;
        if (loading) {
          this.state[fieldName].errorMessages = [];
        }
        this.updateFieldInputs(fieldName);
        if (recurring) {
          this.setFieldsLoading(
            this.subscriptions[fieldName] || [],
            loading,
            recurring,
            Array.from(new Set([...triggerFields, fieldName])) as FieldNames
          );
        }
      }
    }
  }

  /**
   * Sets the value of a specific field in the form using the DataManager,
   * then triggers validation and state updates for the field and any subscribed
   * fields. It also manages the loading status for the field during the update
   * process to provide feedback on the ongoing operation.
   *
   * @param name - The name of the field to set the value for.
   * @param value - The value to set for the specified field.
   */
  public async setFieldValue(
    name: FieldName,
    value: FieldValue
  ) {
    if (!this.loading && value !== (this.dataManager.getFieldValue(name) ?? EMPTY_VALUE)) {
      this.setFieldsLoading(
        [name],
        true,
        true
      );
      await this.dataManager.setFieldValue(name, value);
      const values = this.dataManager.getValues() || {};
      await this.validationManager.validateField(name, values);
      await this.updateFieldState(name, values);
      this.updateFieldInputs(name);
      this.setFieldsLoading(
        [name],
        false,
        true
      );
      await this.updateCustomState();
    }
  };

  /**
   * Overwrites the values of multiple fields at once, then recomputes data,
   * validation and form state for the whole form. Provided values are merged over
   * the current form values (fields not included keep their current value), so a
   * partial patch is safe. Fields carrying a computed value (data.assignedValue)
   * are recomputed rather than overwritten, matching setFieldValue/init behaviour.
   *
   * @param values - Map of field names to the values to set.
   */
  public async updateValues(
    values: FieldValues
  ) {
    if (this.loading) {
      return;
    }
    this.setLoading(true);
    const mergedValues: FieldValues = {
      ...(this.dataManager.getValues() ?? {}),
      ...values,
    };
    await this.dataManager.updateState(mergedValues);
    const sanitizedValues = this.dataManager.getValues() || {};
    await this.validationManager.updateState(sanitizedValues);
    await this.updateState(sanitizedValues);
    this.setLoading(false);
  };

  /**
   * Registers a callback function for updating the input representation of
   * a specific field. Whenever the state of the field changes, the registered
   * callbacks will be triggered to ensure that the input representation reflects
   * the latest state.
   *
   * @param name - The name of the field to register the input callback for.
   * @param callback - The callback function to be triggered when the field
   *                   state changes, responsible for updating the input
   *                   representation of the field.
   */
  public registerFieldInput(
    name: FieldName,
    callback: () => void
  ) {
    if (this.fieldInputs[name]) {
      this.fieldInputs[name].push(callback);
    } else {
      this.fieldInputs[name] = [callback];
    }
  };

  /**
   * Registers a callback function for updating the form instance representation
   * whenever the form state changes.
   *
   * @param id - The identifier for the form instance to register the callback for.
   * @param callback - The callback function to be triggered when the form state changes.
   */
  public registerFormInstance(
    id: string,
    callback: () => void
  ) {
    this.formInstances[id] = callback;
  };
}
